import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'

import { agentsApiSlice } from './agents-api.store'
import { apiSlice } from './api.store'
import { createWebSocketMiddleware } from './middleware/websocket.middleware'
import { authReducer } from './slices/auth.slice'
import { billingReducer } from './slices/billing.slice'
import { billingUiReducer } from './slices/billing-ui.slice'
import { chatReducer } from './slices/chat.slice'
import { draftAgencyChatReducer } from './slices/draft-agency-chat.slice'
import { splitViewReducer } from './slices/split-view.slice'
import { uiReducer } from './slices/ui.slice'

const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  [agentsApiSlice.reducerPath]: agentsApiSlice.reducer,
  auth: authReducer,
  billing: billingReducer,
  billingUi: billingUiReducer,
  ui: uiReducer,
  chat: chatReducer,
  splitView: splitViewReducer,
  draftAgencyChat: draftAgencyChatReducer,
})

export interface StoreConfig {
  devTools?: boolean
}

export const createStore = (config?: StoreConfig) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredPaths: ['splitView.contentData'],
          ignoredActions: ['splitView/openSplitView', 'splitView/updateSplitViewContent'],
        },
      })
        .concat(apiSlice.middleware)
        .concat(agentsApiSlice.middleware)
        .concat(createWebSocketMiddleware()),
    devTools: config?.devTools ?? true,
  })

  setupListeners(store.dispatch)

  return store
}

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof createStore>
export type AppDispatch = AppStore['dispatch']

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
